import {
  BreakpointObserver,
  BreakpointState,
  Breakpoints,
} from '@angular/cdk/layout';
import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { MuniSupportService } from '@app/core/api/api/muni-support.service';
import { SurveyData } from '@app/core/models/survey-settings.model';
import { ChatbotDialogComponent } from '@app/shared/components/chatbot-dialog/chatbot-dialog.component';
import { FeedbackDialogComponent } from '@app/shared/components/feeedback-dialog/feedback-dialog.component';
import { SurveyActions, SurveySelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { SUPPORT_TYPES } from '@app/shared/app.constants';
import { ClientServicesDialog } from '../../components/client-services-dialog/client-services-dialog';

@Component({
  selector: 'app-select-municipality',
  templateUrl: './select-municipality.component.html',
  styleUrls: ['./select-municipality.component.scss'],
})
export class SelectMunicipalityComponent implements OnInit {
  survey$: Observable<SurveyData>;
  subscriptions: Subscription = new Subscription();
  municipalitiesDropdownOptions: {
    value: number;
    text: string;
    state: string;
    options: string[];
  }[];
  selectedClient = null;
  isExtraSmall: Observable<BreakpointState> = this.breakpointObserver.observe(
    Breakpoints.XSmall
  );
  dialogRef: MatDialogRef<ChatbotDialogComponent, any> = null;
  feedDialogRef: MatDialogRef<FeedbackDialogComponent, any> = null;
  surveyLink: string = null;

  @ViewChild('pop', { static: false }) pop: any;

  ngAfterViewInit(): void {
    const isChatBotDialogShown = localStorage.getItem('isChatBotDialogShown');

    if (isChatBotDialogShown === null) {
      this.openChatbotDialog();

      setTimeout(() => {
        this.dialogRef.close();
      }, 8000);
    } else {
      this.pop.show();
    }
  }

  constructor(
    private muniSupportService: MuniSupportService,
    private store: Store<AppState>,
    private router: Router,
    public dialog: MatDialog,
    private breakpointObserver: BreakpointObserver,
    private datePipe: DatePipe
  ) {
    this.survey$ = this.store.select(SurveySelectors.getActiveSurveyInfo);

    this.municipalitiesDropdownOptions =
      this.muniSupportService.getMunicipalities();
  }

  ngOnInit() {
    this.subscriptions.add(
      this.store
        .select(SurveySelectors.getActiveSurveyInfo)
        .subscribe(survey => {
          if (survey) {
            const surveyStartDate = new Date(
              this.datePipe.transform(
                survey.publicSurveyDateRange?.startDate,
                'yyyy-MM-ddTHH:mm:ssZ'
              )
            );
            const surveyEndDate = new Date(
              this.datePipe.transform(
                survey.publicSurveyDateRange?.endDate,
                'yyyy-MM-ddTHH:mm:ssZ'
              )
            );
            const currentUtcDate = new Date(new Date().toUTCString());
            const currentUtcDateString = currentUtcDate.toISOString();

            if (
              currentUtcDateString >= surveyStartDate.toISOString() &&
              currentUtcDateString <= surveyEndDate.toISOString()
            ) {
              this.surveyLink = survey.publicSurveyDateRange?.surveyLink;
            }
          }
        })
    );

    // Get survey details only if the survey link is null
    if (this.surveyLink === null) {
      this.store.dispatch(SurveyActions.getSurveySettings());
    }

    this.pop.show();
  }

  buttonClicked(
    option:
      | 'Missed Service'
      | 'Bulk Pickup'
      | 'ETA Request'
      | 'Schedule a Rear Yard Pickup'
  ) {
    if (option !== 'Missed Service') {
      this.dialog.open(ClientServicesDialog, {
        maxWidth: '44.6875rem',
        panelClass: 'muni-support-dialog',
        data: {
          selectedClient: this.selectedClient,
          selectedOption: option,
        },
      });
    } else if (this.selectedClient) {
      // Route to missed service and update state and city accordingly
      this.router.navigate(
        [
          'muni-support',
          this.selectedClient.value,
          Number(SUPPORT_TYPES.MISSED_SERVICE),
        ],
        { queryParams: { hasBackButton: true } }
      );
    }
  }

  openChatbotDialog(): void {
    const audio = new Audio('../assets/sound/RTS_chat_modern_chime_sound.mp3');

    audio.play();

    this.dialogRef = this.dialog.open(ChatbotDialogComponent, {
      maxWidth: '100vw',
      maxHeight: '100vh',
      width: '25rem',
      height: '31.25rem',
      panelClass: 'chatbot-dialog-container',
      position: {
        bottom: '3.4375rem',
        right: '1.25rem',
      },
    });

    localStorage.setItem('isChatBotDialogShown', 'true');

    const smallDialogSubscription = this.isExtraSmall.subscribe(size => {
      if (size.matches) {
        this.dialogRef.updateSize(
          'calc(99vw - .3125rem)',
          'calc(99vh - .3125rem)'
        );
        this.dialogRef.updatePosition({
          bottom: '.3125rem',
          right: '.3125rem',
        });
      }
    });

    this.dialogRef.afterClosed().subscribe(() => {
      smallDialogSubscription.unsubscribe();
    });
  }

  openFeedbackDialog(surveyLink: string): void {
    this.feedDialogRef = this.dialog.open(FeedbackDialogComponent, {
      data: { surveyLink: surveyLink },
      width: '90%',
      maxWidth: '100vw',
      maxHeight: '100vh',
    });

    const smallFBDialogSubscription = this.isExtraSmall.subscribe(size => {
      if (size.matches) {
        this.feedDialogRef.updateSize(
          'calc(99vw - .3125rem)',
          'calc(99vh - .3125rem)'
        );
        this.feedDialogRef.updatePosition({
          bottom: '.3125rem',
          right: '.3125rem',
        });
      }
    });

    this.feedDialogRef.afterClosed().subscribe(() => {
      smallFBDialogSubscription.unsubscribe();
    });
  }
}
